import React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import 'react-table/react-table.css';
import ReactTable, {
  Column,
  Filter,
  FilteredChangeFunction,
  PageChangeFunction,
  PageSizeChangeFunction,
  SortedChangeFunction,
  SortingRule
} from 'react-table';
import styled from 'styled-components';

const StyledTable = styled(ReactTable)`
  .rt-thead.-filters select {
    padding: 2px 7px 3px 7px;
  }
  .rt-tr.clickable {
    cursor: pointer;
  }
`;

export interface ManagedTableStateProps {
  page?: number;
  pageSize?: number;
  sorted: SortingRule[];
  filtered: Filter[];
  onPageChange: PageChangeFunction;
  onPageSizeChange: PageSizeChangeFunction;
  onSortedChange: SortedChangeFunction;
  onFilteredChange: FilteredChangeFunction;
}

interface Props extends WithTranslation {
  data: any;
  columns: Column[];
  defaultPageSize?: number;
  defaultSortColumn?: string;
  defaultSortDesc?: boolean;
  showPagination?: boolean;
  showPageSizeOptions?: boolean;
  filterable?: boolean;
  rowSelector?: (RowInfo: any) => boolean;
  inactiveRowSelector?: (RowInfo: any) => boolean;
  onRowSelected?: (rowInfo: any) => any;
  onDoubleClick?: (rowInfo: any) => any;
  noDataText?: string;

  managed?: ManagedTableStateProps;
}

const Table: React.FunctionComponent<Props> = ({
  columns,
  data,
  defaultPageSize,
  defaultSortColumn,
  defaultSortDesc,
  onRowSelected,
  onDoubleClick,
  rowSelector,
  inactiveRowSelector,
  filterable = true,
  t,
  noDataText,
  showPagination = true,
  showPageSizeOptions = true,
  managed
}) => {
  const defaultSorted = [];

  if (defaultSortColumn) {
    defaultSorted.push({
      id: defaultSortColumn,
      desc: defaultSortDesc || false
    });
  }

  const isClickable = !!onRowSelected || !!onDoubleClick;

  return (
    <StyledTable
      getTdProps={(state, rowInfo, column, instance) => {
        return {
          onClick: (e, handleOriginal) => {
            if (onRowSelected && rowInfo) {
              onRowSelected(rowInfo);
            }

            // IMPORTANT! React-Table uses onClick internally to trigger
            // events like expanding SubComponents and pivots.
            // By default a custom 'onClick' handler will override this functionality.
            // If you want to fire the original onClick handler, call the
            // 'handleOriginal' function.
            if (handleOriginal) {
              handleOriginal();
            }
          },

          onDoubleClick: (e, handleOriginal) => {
            if (onDoubleClick && rowInfo) {
              onDoubleClick(rowInfo);
            }

            // // IMPORTANT! React-Table uses onClick internally to trigger
            // // events like expanding SubComponents and pivots.
            // // By default a custom 'onClick' handler will override this functionality.
            // // If you want to fire the original onClick handler, call the
            // // 'handleOriginal' function.
            if (handleOriginal) {
              handleOriginal();
            }
          }
        };
      }}
      getTrProps={(state, rowInfo, column) => {
        const isInactive = rowInfo && rowInfo.row && inactiveRowSelector && inactiveRowSelector(rowInfo);

        const fontStyle = isInactive ? 'italic' : 'normal';

        if (rowInfo && rowInfo.row && rowSelector && rowSelector(rowInfo)) {
          return {
            className: isClickable && !isInactive ? 'clickable' : null,
            style: {
              background: '#00afec',
              color: isInactive ? '#999' : 'white',
              fontStyle
            }
          };
        }
        return {
          className: isClickable && !isInactive ? 'clickable' : null,
          style: {
            color: isInactive ? '#999' : null,
            fontStyle
          }
        };
      }}
      data={data}
      columns={columns}
      // managed state
      onPageChange={(managed && managed.onPageChange) || undefined}
      onPageSizeChange={(managed && managed.onPageSizeChange) || undefined}
      onFilteredChange={(managed && managed.onFilteredChange) || undefined}
      onSortedChange={(managed && managed.onSortedChange) || undefined}
      page={managed && typeof managed.page === 'number' ? managed.page : undefined}
      pageSize={(managed && managed.pageSize) || undefined}
      sorted={(managed && managed.sorted) || undefined}
      filtered={(managed && managed.filtered) || undefined}
      // managed state
      defaultPageSize={defaultPageSize}
      filterable={filterable}
      defaultSorted={defaultSorted}
      showPagination={showPagination}
      showPageSizeOptions={showPageSizeOptions}
      previousText={t('table.previous')}
      nextText={t('table.next')}
      pageText={t('table.page')}
      ofText={t('table.of')}
      rowsText={t('table.rows')}
      defaultFilterMethod={(filter, row) =>
        String(row[filter.id] !== null ? row[filter.id].toLowerCase() : null).indexOf(filter.value.toLowerCase()) > -1
      }
      className={'-striped -highlight'}
      noDataText={noDataText}
    />
  );
};

export default withTranslation()(Table);
